/**
 * Pure parts of the car's price display (P5-c): the daily prices the API computed, or the reason there are none. The API owns the
 * numbers; this only words them.
 */

import { formatInt } from '@/lib/format';
import { cislo } from './cenik-helpers';
import type { CenaRozpis, FleetEntry, Rezervace, RucniSleva } from './api';

export const CENY_CHYBI_TEXT: Record<NonNullable<FleetEntry['ceny_chybi']>, string> = {
  vazba: 'Vozidlo nelze ve skladu načíst',
  bez_tarifu: 'Vůz nemá tarif',
  bez_ceniku: 'Tarif nemá ceník',
  bez_ceny_vozidla: 'Vozidlo nemá prodejní cenu',
};

const kc = (n: number) => `${formatInt(n)} Kč`;

export type CenyZobrazeni = { ok: true; hlavni: string; detail: string } | { ok: false; chyba: string };

/** The guest price as the headline and the loyal groups (and the overhead price) under it; or why there is no price. */
export function cenyZobrazeni(e: Pick<FleetEntry, 'ceny' | 'ceny_chybi'>): CenyZobrazeni {
  if (!e.ceny) return { ok: false, chyba: e.ceny_chybi ? CENY_CHYBI_TEXT[e.ceny_chybi] : 'Bez ceny' };
  const c = e.ceny;
  return { ok: true, hlavni: kc(c.cena5), detail: `skupiny ${formatInt(c.cena2)} · ${formatInt(c.cena3)} · ${formatInt(c.cena4)} · režijní ${formatInt(c.cena1)}` };
}

// ---------- The price of a rental (P5-f1) ----------

export interface SlevaForm { jednotka: RucniSleva['jednotka']; hodnota: string }

export const SLEVA_JEDNOTKY: { value: RucniSleva['jednotka']; label: string }[] = [
  { value: 'kc', label: 'Kč' },
  { value: 'kc_den', label: 'Kč za den' },
  { value: 'proc', label: '%' },
];

export const SKUPINA_TEXT: Record<CenaRozpis['skupina'], string> = {
  5: 'host / nový klient',
  2: 'klient s jedním pronájmem',
  3: 'věrný klient (více pronájmů, nebo koupil vůz)',
  4: 'věrný klient podle útraty',
};

export const slevaSeed = (r: Pick<Rezervace, 'cena_vstup'> | null): SlevaForm => {
  const s = r?.cena_vstup.sleva;
  return { jednotka: s?.jednotka ?? 'kc', hodnota: s ? String(s.hodnota) : '' };
};

/** The typed discount: an empty box or 0 is no discount; a non-number is named. */
export function slevaZFormulare(f: SlevaForm): { ok: true; sleva: RucniSleva | null } | { ok: false; chyba: string } {
  const n = cislo(f.hodnota);
  if (n === null || n === 0) return { ok: true, sleva: null };
  if (Number.isNaN(n)) return { ok: false, chyba: `Sleva: „${f.hodnota}“ není číslo.` };
  return { ok: true, sleva: { jednotka: f.jednotka, hodnota: n } };
}

/** The deposit of one rental: the box is empty for the car's own, or whole Kč (0 = no deposit). */
export const KAUCE_MAX = 1_000_000;
export const kauceSeed = (r: Pick<Rezervace, 'cena_vstup'> | null): string => (r?.cena_vstup.kauce != null ? String(r.cena_vstup.kauce) : '');

/** The typed deposit: empty = the car's own (`null`); whole Kč up to the API's limit; anything else is named. */
export function kauceZFormulare(text: string): { ok: true; kauce: number | null } | { ok: false; chyba: string } {
  const t = text.replace(/[\s\u00a0]/g, '');
  if (t === '') return { ok: true, kauce: null };
  if (!/^\d+$/.test(t) || Number(t) > KAUCE_MAX) {
    return { ok: false, chyba: `Kauce: „${text.trim()}“ není celé číslo v Kč od 0 do ${formatInt(KAUCE_MAX)} (prázdné = kauce vozu).` };
  }
  return { ok: true, kauce: Number(t) };
}

/** Two manual discounts are the same (key order is irrelevant: the API serves a stored one in the JSON column's order). */
export const stejnaSleva = (a: RucniSleva | null | undefined, b: RucniSleva | null | undefined): boolean =>
  a == null || b == null ? a == null && b == null : a.jednotka === b.jednotka && a.hodnota === b.hodnota;

/**
 * The price inputs for the request. A new reservation sends what is set; a change sends only what differs from the stored reservation
 * (absent = keep, `sleva: null` = remove), so saving a note never writes back a discount someone else changed.
 */
export function cenaBody(
  stored: Pick<Rezervace, 'cena_vstup'> | null,
  zahranici: boolean,
  sleva: RucniSleva | null,
  kauce: number | null,
): { zahranici?: boolean; sleva?: RucniSleva | null; kauce?: number | null } {
  const was = stored?.cena_vstup;
  const body: { zahranici?: boolean; sleva?: RucniSleva | null; kauce?: number | null } = {};
  if (stored ? zahranici !== (was?.zahranici ?? false) : zahranici) body.zahranici = zahranici;
  if (stored ? !stejnaSleva(sleva, was?.sleva) : sleva != null) body.sleva = sleva;
  if (stored ? kauce !== (was?.kauce ?? null) : kauce != null) body.kauce = kauce;
  return body;
}

export interface CenaRadek { label: string; hodnota: string; silne?: boolean }

/** The breakdown as lines for the form: what was multiplied, what was added or taken off, the total. */
export function cenaRadky(c: CenaRozpis): CenaRadek[] {
  const r: CenaRadek[] = [];
  r.push(c.pausal
    ? { label: `Paušál (do ${c.pausal.dny} dní)`, hodnota: kc(c.najem) }
    : { label: `Pronájem ${c.dny} × ${kc(c.cena_den)}`, hodnota: kc(c.najem) });
  if (c.zahranici > 0) r.push({ label: 'Příplatek za zahraničí', hodnota: kc(c.zahranici) });
  if (c.sleva_kc > 0) r.push({ label: 'Sleva', hodnota: `−${kc(c.sleva_kc)}` });
  r.push({ label: 'Pronájem celkem', hodnota: kc(c.pronajem), silne: true });
  if (c.kauce > 0) r.push({ label: 'Kauce', hodnota: kc(c.kauce) });
  r.push({ label: 'Celkem k úhradě', hodnota: kc(c.celkem), silne: true });
  return r;
}

/** The inline hint for a discount that can only be a typo (the server refuses it too). */
export const slevaHint = (f: SlevaForm): string | null => {
  const n = cislo(f.hodnota);
  if (n === null || Number.isNaN(n)) return null;
  if (f.jednotka === 'proc' && n > 100) return 'Sleva v procentech nemůže být přes 100 %.';
  if (f.jednotka !== 'proc' && !Number.isInteger(n)) return 'Sleva v Kč musí být celé číslo.';
  return null;
};
